import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  Truck, Plus, Search, RefreshCw, Fuel, Wrench, MapPin, User,
  AlertTriangle, CheckCircle2, Clock, ChevronRight, Calendar,
  FileText, DollarSign, Gauge, X, Camera, Mic, Upload, ArrowRight,
  ArrowLeftRight, Shield, FileWarning, Play, Square, Eye
} from "lucide-react";
import { toast } from "sonner";

// =============================================================================
// CONSTANTS
// =============================================================================
const VEHICLE_TYPES = [
  { value: "car", label: "Car" },
  { value: "van", label: "Van" },
  { value: "pickup", label: "Pickup" },
  { value: "bike", label: "Bike" },
  { value: "tempo", label: "Tempo" },
  { value: "truck", label: "Truck" },
  { value: "toto", label: "Toto" },
  { value: "3_wheeler", label: "3 Wheeler" },
  { value: "4_wheeler", label: "4 Wheeler" },
  { value: "other", label: "Other" },
];

const FUEL_TYPES = [
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "cng", label: "CNG" },
  { value: "electric", label: "Electric" },
];

const OWNERSHIP_TYPES = [
  { value: "company", label: "Company Owned" },
  { value: "vendor", label: "Vendor Owned" },
];

const MOVEMENT_PURPOSES = [
  { value: "installation", label: "Installation" },
  { value: "material_inwards", label: "Material Inwards" },
  { value: "material_return", label: "Material Return" },
  { value: "site_visit", label: "Site Visit" },
  { value: "other", label: "Other" },
];

const PAYMENT_MODES = [
  { value: "company_upi", label: "Company UPI" },
  { value: "personal_reimbursable", label: "Personal (Reimbursable)" },
  { value: "petty_cash", label: "Petty Cash" },
  { value: "fuel_card", label: "Fuel Card" },
];

const ISSUE_TYPES = [
  { value: "preventive", label: "Preventive" },
  { value: "accident", label: "Accident" },
  { value: "general_service", label: "General Service" },
  { value: "breakdown", label: "Breakdown" },
  { value: "tyre_issue", label: "Tyre Issue" },
  { value: "battery_issue", label: "Battery Issue" },
  { value: "other", label: "Other" },
];

const SEVERITY_LEVELS = [
  { value: "can_drive", label: "Can Still Drive", color: "text-green-600 bg-green-50" },
  { value: "needs_service_soon", label: "Needs Service Soon", color: "text-yellow-600 bg-yellow-50" },
  { value: "cannot_drive", label: "Cannot Drive", color: "text-red-600 bg-red-50" },
];

const STATUS_COLORS = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
  under_maintenance: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Maintenance" },
  breakdown: { bg: "bg-red-100", text: "text-red-700", label: "Breakdown" },
  inactive: { bg: "bg-slate-100", text: "text-slate-700", label: "Inactive" },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function VehiclesPage() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const isVehicleIncharge = user?.role === "vehicle_incharge";
  const isAdmin = ["admin", "package_head"].includes(user?.role);
  const isSeniorRole = ["admin", "package_head", "ci", "supervisor"].includes(user?.role);
  const canManage = isAdmin || isVehicleIncharge;
  const canAssignDrivers = isSeniorRole || isVehicleIncharge; // CI, Supervisor, Admin, PH, Vehicle Incharge can assign drivers

  const [activeTab, setActiveTab] = useState(isDriver ? "my-vehicle" : (canAssignDrivers && !canManage ? "drivers" : "vehicles"));
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myVehicle, setMyVehicle] = useState(null);
  const [circles, setCircles] = useState([]);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showMovementLog, setShowMovementLog] = useState(null);
  const [showRefuelLog, setShowRefuelLog] = useState(null);
  const [showMaintenanceIssue, setShowMaintenanceIssue] = useState(null);
  const [showMaintenanceLog, setShowMaintenanceLog] = useState(null);
  const [showTransfer, setShowTransfer] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (isDriver) {
        const res = await api.get("/vehicles/my-vehicle");
        setMyVehicle(res.data);
      } else {
        const [vehiclesRes, statsRes, circlesRes] = await Promise.all([
          api.get("/vehicles"),
          api.get("/vehicles/stats"),
          api.get("/hierarchy/circles").catch(() => ({ data: [] }))
        ]);
        setVehicles(vehiclesRes.data.items || []);
        setStats(statsRes.data);
        setCircles(circlesRes.data || []);
      }
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [isDriver]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const TABS = isDriver
    ? [
        { id: "my-vehicle", label: "My Vehicle", icon: Truck },
        { id: "movement", label: "Movement", icon: MapPin },
        { id: "refuel", label: "Refuel", icon: Fuel },
        { id: "issues", label: "Report Issue", icon: AlertTriangle },
      ]
    : canManage
    ? [
        { id: "vehicles", label: "Vehicles", icon: Truck },
        { id: "movements", label: "Movements", icon: MapPin },
        { id: "refuels", label: "Refuels", icon: Fuel },
        { id: "issues", label: "Issues", icon: AlertTriangle },
        { id: "maintenance", label: "Maintenance", icon: Wrench },
        { id: "transfers", label: "Transfers", icon: ArrowLeftRight },
        { id: "drivers", label: "Drivers", icon: User },
      ]
    : canAssignDrivers
    ? [
        { id: "drivers", label: "Driver Assignments", icon: User },
        { id: "vehicles", label: "Vehicles", icon: Truck },
      ]
    : [
        { id: "vehicles", label: "Vehicles", icon: Truck },
        { id: "movements", label: "Movements", icon: MapPin },
        { id: "refuels", label: "Refuels", icon: Fuel },
      ];

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            {isDriver ? "My Vehicle" : "Vehicle Management"}
          </h1>
          <p className="text-slate-500 text-sm">
            {isDriver ? "Log trips, refuels & report issues" : `${vehicles.length} vehicles · Fleet management`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50" data-testid="refresh-btn">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
          {canManage && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
              data-testid="add-vehicle-btn"
            >
              <Plus size={16} />Add Vehicle
            </button>
          )}
        </div>
      </div>

      {/* Stats (non-driver) */}
      {!isDriver && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total" value={stats.total_vehicles} color="text-slate-800" />
          <StatCard label="Active" value={stats.active} color="text-green-600" bgColor="bg-green-50" />
          <StatCard label="Maintenance" value={stats.under_maintenance} color="text-yellow-600" bgColor="bg-yellow-50" />
          <StatCard label="Breakdown" value={stats.breakdown} color="text-red-600" bgColor="bg-red-50" />
          <StatCard label="Fuel (Month)" value={`₹${((stats.fuel_expense_this_month || 0)/1000).toFixed(1)}K`} color="text-purple-600" bgColor="bg-purple-50" />
        </div>
      )}

      {/* Alerts */}
      {!isDriver && stats && (stats.fitness_expiring_soon > 0 || stats.insurance_expiring_soon > 0 || stats.open_maintenance_issues > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 mb-4 flex items-start gap-2">
          <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
          <div className="text-sm text-yellow-700">
            {stats.fitness_expiring_soon > 0 && <span className="mr-2">⚠️ {stats.fitness_expiring_soon} Fitness expiring</span>}
            {stats.insurance_expiring_soon > 0 && <span className="mr-2">⚠️ {stats.insurance_expiring_soon} Insurance expiring</span>}
            {stats.open_maintenance_issues > 0 && <span>🔧 {stats.open_maintenance_issues} open issues</span>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === tab.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {isDriver ? (
        <>
          {activeTab === "my-vehicle" && <DriverVehicleView vehicle={myVehicle} loading={loading} onRefresh={fetchData} />}
          {activeTab === "movement" && <DriverMovementTab vehicle={myVehicle?.vehicle} onRefresh={fetchData} />}
          {activeTab === "refuel" && <DriverRefuelTab vehicle={myVehicle?.vehicle} onRefresh={fetchData} />}
          {activeTab === "issues" && <DriverIssueTab vehicle={myVehicle?.vehicle} onRefresh={fetchData} />}
        </>
      ) : (
        <>
          {activeTab === "vehicles" && (
            <VehicleListTab
              vehicles={vehicles}
              loading={loading}
              onSelect={setShowDetail}
              canManage={canManage}
            />
          )}
          {activeTab === "movements" && <MovementListTab />}
          {activeTab === "refuels" && <RefuelListTab />}
          {activeTab === "issues" && <IssueListTab canManage={canManage} />}
          {activeTab === "maintenance" && <MaintenanceListTab vehicles={vehicles} onCreateNew={setShowMaintenanceLog} />}
          {activeTab === "transfers" && <TransferListTab circles={circles} vehicles={vehicles} onCreateNew={setShowTransfer} canApprove={isAdmin} />}
          {activeTab === "drivers" && <DriverListTab />}
        </>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateVehicleModal circles={circles} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} />
      )}
      {showDetail && (
        <VehicleDetailModal
          vehicleId={showDetail}
          canManage={canManage}
          onClose={() => setShowDetail(null)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

// =============================================================================
// STAT CARD
// =============================================================================
function StatCard({ label, value, color = "text-slate-800", bgColor = "bg-white" }) {
  return (
    <div className={`${bgColor} border border-slate-200 rounded-sm p-3`}>
      <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">{label}</div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}

// =============================================================================
// DRIVER VIEWS
// =============================================================================
function DriverVehicleView({ vehicle, loading, onRefresh }) {
  if (loading) return <LoadingState />;
  if (!vehicle?.vehicle) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <Truck size={48} className="mx-auto mb-4 text-slate-300" />
        <h3 className="text-lg font-bold text-slate-700 mb-2">No Vehicle Assigned</h3>
        <p className="text-slate-500 text-sm">Contact your Vehicle Incharge to get a vehicle assigned.</p>
      </div>
    );
  }

  const v = vehicle.vehicle;
  const status = STATUS_COLORS[v.status] || STATUS_COLORS.inactive;

  return (
    <div className="space-y-4">
      {/* Vehicle Card */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Truck className="text-slate-600" size={32} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-black text-slate-900">{v.registration_number}</h2>
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${status.bg} ${status.text}`}>
                {status.label}
              </span>
            </div>
            <p className="text-slate-600 text-sm">{v.make} {v.model} • {v.vehicle_type}</p>
            <p className="text-slate-500 text-xs mt-1">
              <span className="font-medium">Fuel:</span> {v.fuel_type?.toUpperCase()} •{" "}
              <span className="font-medium">Odometer:</span> {v.current_odometer?.toLocaleString()} km
            </p>
          </div>
        </div>

        {/* Documents Alert */}
        {(isExpiringSoon(v.fitness_expiry) || isExpiringSoon(v.insurance_expiry)) && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-sm p-3 text-sm text-yellow-700">
            <AlertTriangle size={14} className="inline mr-1" />
            {isExpiringSoon(v.fitness_expiry) && <span>Fitness expires: {v.fitness_expiry}</span>}
            {isExpiringSoon(v.fitness_expiry) && isExpiringSoon(v.insurance_expiry) && " • "}
            {isExpiringSoon(v.insurance_expiry) && <span>Insurance expires: {v.insurance_expiry}</span>}
          </div>
        )}
      </div>

      {/* 9 PM Warning Banner for Unclosed Movement */}
      {vehicle.has_open_movement && vehicle.today_movement && new Date().getHours() >= 21 && (
        <div className="bg-red-500 text-white rounded-sm p-4 animate-pulse" data-testid="late-movement-warning">
          <div className="flex items-center gap-3">
            <AlertTriangle size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ Close Your Movement Log!</p>
              <p className="text-xs opacity-90">
                It's past 9 PM. Please close today's movement log with your final odometer reading before end of day.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Today's Movement Status */}
      {vehicle.has_open_movement && vehicle.today_movement && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-700 font-bold text-sm">Movement In Progress</p>
              <p className="text-blue-600 text-xs">
                Started at {vehicle.today_movement.opening_odometer?.toLocaleString()} km • {vehicle.today_movement.purpose?.replace("_", " ")}
              </p>
            </div>
            <div className="flex items-center gap-1 text-blue-600">
              <Clock size={16} />
              <span className="text-xs font-medium">Open</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DriverMovementTab({ vehicle, onRefresh }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showClose, setShowClose] = useState(null);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/vehicles/movements/list", { params: { limit: 20 } });
      setMovements(res.data.items || []);
    } catch { toast.error("Failed to load movements"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);

  const openMovement = movements.find(m => m.status === "open");

  if (!vehicle) {
    return <div className="text-center py-8 text-slate-400">No vehicle assigned</div>;
  }

  return (
    <div className="space-y-4">
      {/* 9 PM Warning Banner */}
      {openMovement && new Date().getHours() >= 21 && (
        <div className="bg-red-500 text-white rounded-sm p-4 animate-pulse" data-testid="late-movement-warning-tab">
          <div className="flex items-center gap-3">
            <AlertTriangle size={24} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-sm">⚠️ Close Your Movement Log Now!</p>
              <p className="text-xs opacity-90">
                It's past 9 PM. Please enter your final odometer reading and close today's trip.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        {openMovement ? (
          <button
            onClick={() => setShowClose(openMovement)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-sm font-bold text-sm hover:bg-red-600"
            data-testid="close-movement-btn"
          >
            <Square size={16} />
            Close Movement Log
          </button>
        ) : (
          <button
            onClick={() => setShowCreate(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-sm font-bold text-sm hover:bg-green-600"
            data-testid="start-movement-btn"
          >
            <Play size={16} />
            Start Daily Movement
          </button>
        )}
      </div>

      {/* Movement List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-700 text-sm">Recent Movements</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="animate-spin mx-auto text-orange-500" /></div>
        ) : movements.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No movements logged yet</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {movements.map(m => (
              <div key={m.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{m.log_date} • {m.purpose?.replace("_", " ")}</p>
                  <p className="text-xs text-slate-500">
                    {m.opening_odometer?.toLocaleString()} → {m.closing_odometer?.toLocaleString() || "..."} km
                    {m.distance_km && <span className="font-bold text-orange-600 ml-2">{m.distance_km} km</span>}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.status === "open" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && (
        <MovementLogModal
          vehicle={vehicle}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchMovements(); onRefresh?.(); }}
        />
      )}
      {showClose && (
        <CloseMovementModal
          movement={showClose}
          onClose={() => setShowClose(null)}
          onClosed={() => { setShowClose(null); fetchMovements(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

function DriverRefuelTab({ vehicle, onRefresh }) {
  const [refuels, setRefuels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchRefuels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/vehicles/refuels/list", { params: { limit: 20 } });
      setRefuels(res.data.items || []);
    } catch { toast.error("Failed to load refuels"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRefuels(); }, [fetchRefuels]);

  if (!vehicle) {
    return <div className="text-center py-8 text-slate-400">No vehicle assigned</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <button
          onClick={() => setShowCreate(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-sm font-bold text-sm hover:bg-purple-600"
          data-testid="log-refuel-btn"
        >
          <Fuel size={16} />
          Log Refuel
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-700 text-sm">Recent Refuels</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="animate-spin mx-auto text-orange-500" /></div>
        ) : refuels.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No refuels logged yet</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {refuels.map(r => (
              <div key={r.id} className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{r.fuel_date}</p>
                    <p className="text-xs text-slate-500">{r.quantity_liters}L @ ₹{r.price_per_liter}/L</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">₹{r.cost_rs?.toLocaleString()}</p>
                    <p className="text-[10px] text-slate-400">{r.payment_mode?.replace("_", " ")}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <RefuelLogModal
          vehicle={vehicle}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchRefuels(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

function DriverIssueTab({ vehicle, onRefresh }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/vehicles/maintenance-issues/list", { params: { limit: 20 } });
      setIssues(res.data.items || []);
    } catch { toast.error("Failed to load issues"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  if (!vehicle) {
    return <div className="text-center py-8 text-slate-400">No vehicle assigned</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <button
          onClick={() => setShowCreate(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-sm font-bold text-sm hover:bg-red-600"
          data-testid="report-issue-btn"
        >
          <AlertTriangle size={16} />
          Report Maintenance Issue
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-700 text-sm">My Reported Issues</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="animate-spin mx-auto text-orange-500" /></div>
        ) : issues.length === 0 ? (
          <div className="p-8 text-center text-slate-400">No issues reported</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {issues.map(i => (
              <div key={i.id} className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{i.issue_type?.replace("_", " ")}</p>
                    <p className="text-xs text-slate-500 line-clamp-1">{i.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{i.issue_date}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      i.status === "open" ? "bg-red-100 text-red-700" :
                      i.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                      "bg-green-100 text-green-700"
                    }`}>{i.status}</span>
                    <span className={`text-[10px] font-medium ${
                      SEVERITY_LEVELS.find(s => s.value === i.severity)?.color || ""
                    }`}>{i.severity?.replace("_", " ")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <MaintenanceIssueModal
          vehicle={vehicle}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchIssues(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// LIST TABS (Admin/Vehicle Incharge)
// =============================================================================
function VehicleListTab({ vehicles, loading, onSelect, canManage }) {
  const [filter, setFilter] = useState({ status: "" });

  const filtered = vehicles.filter(v => !filter.status || v.status === filter.status);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <select
          className="field-select h-9 text-xs"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="under_maintenance">Under Maintenance</option>
          <option value="breakdown">Breakdown</option>
        </select>
        <span className="text-xs text-slate-400">{filtered.length} vehicles</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Truck size={32} className="mx-auto mb-2" />
            No vehicles found
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(v => (
              <div
                key={v.id}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => onSelect(v.id)}
                data-testid={`vehicle-row-${v.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Truck className="text-slate-600" size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800 text-sm">{v.registration_number}</h3>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${STATUS_COLORS[v.status]?.bg} ${STATUS_COLORS[v.status]?.text}`}>
                          {STATUS_COLORS[v.status]?.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{v.make} {v.model} • {v.fuel_type} • {v.circle_name}</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MovementListTab() {
  const [movements, setMovements] = useState([]);
  const [unclosed, setUnclosed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ vehicle_id: "", date_from: "", date_to: "" });
  const [showMap, setShowMap] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.vehicle_id) params.vehicle_id = filters.vehicle_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const [movRes, unclosedRes, vehRes] = await Promise.all([
        api.get("/vehicles/movements/list", { params }),
        api.get("/vehicles/movements/unclosed").catch(() => ({ data: { unclosed_movements: [] } })),
        api.get("/vehicles", { params: { limit: 200 } })
      ]);
      setMovements(movRes.data.items || []);
      setUnclosed(unclosedRes.data.unclosed_movements || []);
      setVehicles(vehRes.data.items || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unclosedIds = new Set(unclosed.map(u => u.id));

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {/* Unclosed trips alert */}
      {unclosed.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle size={16} />
            <span className="font-bold text-sm">{unclosed.length} Unclosed Trip{unclosed.length > 1 ? 's' : ''}</span>
          </div>
          <div className="mt-2 text-xs text-red-600">
            {unclosed.slice(0, 3).map(u => (
              <span key={u.id} className="mr-3">{u.vehicle_number} - {u.driver_name || 'Unknown'}</span>
            ))}
            {unclosed.length > 3 && <span>+{unclosed.length - 3} more</span>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <select
          className="field-select h-9 text-xs min-w-[180px]"
          value={filters.vehicle_id}
          onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })}
        >
          <option value="">All Vehicles</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>{v.registration_number}</option>
          ))}
        </select>
        <input
          type="date"
          className="field-input h-9 text-xs w-36"
          placeholder="From"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
        />
        <input
          type="date"
          className="field-input h-9 text-xs w-36"
          placeholder="To"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
        />
        <button
          onClick={() => setShowMap(true)}
          className="px-3 py-1.5 bg-blue-500 text-white text-xs font-bold rounded-sm hover:bg-blue-600 flex items-center gap-1"
        >
          <MapPin size={14} /> Map View
        </button>
        <span className="text-xs text-slate-400 ml-auto">{movements.length} movements</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vehicle</th>
              <th>Driver</th>
              <th>Purpose</th>
              <th>Start Location</th>
              <th>End Location</th>
              <th>Distance</th>
              <th>Photos</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {movements.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-slate-400">No movements found</td></tr>
            ) : movements.map(m => (
              <tr key={m.id} className={unclosedIds.has(m.id) ? "bg-red-50" : ""}>
                <td className="text-xs">{m.log_date}</td>
                <td className="font-mono text-xs font-bold">{m.vehicle_number}</td>
                <td className="text-xs">{m.driver_name || "-"}</td>
                <td className="text-xs capitalize">{m.purpose?.replace("_", " ")}</td>
                <td className="text-xs">
                  {m.start_gps_lat && m.start_gps_lng ? (
                    <span className="text-blue-600 cursor-pointer hover:underline" title={`${m.start_gps_lat}, ${m.start_gps_lng}`}>
                      📍 Start
                    </span>
                  ) : "-"}
                </td>
                <td className="text-xs">
                  {m.end_gps_lat && m.end_gps_lng ? (
                    <span className="text-green-600 cursor-pointer hover:underline" title={`${m.end_gps_lat}, ${m.end_gps_lng}`}>
                      📍 End
                    </span>
                  ) : "-"}
                </td>
                <td className="text-xs font-mono">{m.distance_traveled ? `${m.distance_traveled} km` : "-"}</td>
                <td className="text-xs">
                  <div className="flex gap-1">
                    {m.opening_odometer_photo_url ? (
                      <img 
                        src={m.opening_odometer_photo_url} 
                        alt="Start" 
                        className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-blue-200"
                        title="Opening Odometer"
                        onClick={() => window.open(m.opening_odometer_photo_url, '_blank')}
                      />
                    ) : null}
                    {m.closing_odometer_photo_url ? (
                      <img 
                        src={m.closing_odometer_photo_url} 
                        alt="End" 
                        className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-green-200"
                        title="Closing Odometer"
                        onClick={() => window.open(m.closing_odometer_photo_url, '_blank')}
                      />
                    ) : null}
                    {!m.opening_odometer_photo_url && !m.closing_odometer_photo_url && (
                      <span className="text-slate-300">-</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    m.status === "open" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                  }`}>
                    {m.status === "open" ? "🔴 OPEN" : "✅ CLOSED"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Map Modal */}
      {showMap && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-bold">Today's Vehicle Routes</h3>
              <button onClick={() => setShowMap(false)}><X size={18} /></button>
            </div>
            <div className="p-4 h-[500px] flex items-center justify-center bg-slate-100">
              <div className="text-center text-slate-500">
                <MapPin size={48} className="mx-auto mb-2 text-slate-400" />
                <p className="font-medium">Map View</p>
                <p className="text-sm">Integration with Google Maps required</p>
                <p className="text-xs mt-2">Shows routes for {movements.filter(m => m.log_date === new Date().toISOString().slice(0,10)).length} movements today</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RefuelListTab() {
  const [refuels, setRefuels] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [fuelAverages, setFuelAverages] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ vehicle_id: "", date_from: "", date_to: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.vehicle_id) params.vehicle_id = filters.vehicle_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const [refuelRes, vehRes] = await Promise.all([
        api.get("/vehicles/refuels/list", { params }),
        api.get("/vehicles", { params: { limit: 200 } })
      ]);
      
      // Filter out electric vehicles
      const nonElectricVehicles = (vehRes.data.items || []).filter(v => v.fuel_type !== "electric");
      setVehicles(nonElectricVehicles);
      
      // Filter refuels to only non-electric vehicles
      const nonElectricIds = new Set(nonElectricVehicles.map(v => v.id));
      const filteredRefuels = (refuelRes.data.items || []).filter(r => nonElectricIds.has(r.vehicle_id));
      setRefuels(filteredRefuels);
      
      // Fetch fuel averages for vehicles with refuels
      const uniqueVehicleIds = [...new Set(filteredRefuels.map(r => r.vehicle_id))];
      const avgPromises = uniqueVehicleIds.map(id => 
        api.get(`/vehicles/refuels/fuel-average/${id}`).catch(() => null)
      );
      const avgResults = await Promise.all(avgPromises);
      const avgMap = {};
      avgResults.forEach((res, i) => {
        if (res?.data) avgMap[uniqueVehicleIds[i]] = res.data;
      });
      setFuelAverages(avgMap);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3">
        <select
          className="field-select h-9 text-xs min-w-[180px]"
          value={filters.vehicle_id}
          onChange={(e) => setFilters({ ...filters, vehicle_id: e.target.value })}
        >
          <option value="">All Vehicles (Petrol/Diesel/CNG)</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>{v.registration_number} ({v.fuel_type})</option>
          ))}
        </select>
        <input
          type="date"
          className="field-input h-9 text-xs w-36"
          placeholder="From"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
        />
        <input
          type="date"
          className="field-input h-9 text-xs w-36"
          placeholder="To"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
        />
        <span className="text-xs text-slate-400 ml-auto">{refuels.length} refuels</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden overflow-x-auto">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vehicle</th>
              <th>Circle</th>
              <th>Station</th>
              <th>Fuel</th>
              <th>Quantity</th>
              <th>Cost</th>
              <th>Rate/L</th>
              <th>Fuel Avg (km/L)</th>
              <th>Photos</th>
              <th>Payment</th>
            </tr>
          </thead>
          <tbody>
            {refuels.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-slate-400">No refuels found</td></tr>
            ) : refuels.map(r => {
              const avg = fuelAverages[r.vehicle_id];
              const vehicle = vehicles.find(v => v.id === r.vehicle_id);
              const expectedAvg = vehicle?.expected_fuel_average;
              const actualAvg = avg?.actual_average;
              const isBelowExpected = actualAvg && expectedAvg && actualAvg < expectedAvg;
              
              return (
                <tr key={r.id}>
                  <td className="text-xs">{r.fuel_date}</td>
                  <td className="font-mono text-xs font-bold">{r.vehicle_number}</td>
                  <td className="text-xs">{r.circle_name || vehicle?.circle_name || "-"}</td>
                  <td className="text-xs">{r.fuel_station_name || "-"}</td>
                  <td className="text-xs uppercase">{vehicle?.fuel_type || "-"}</td>
                  <td className="text-xs font-mono">{r.quantity_liters} L</td>
                  <td className="text-xs font-bold text-green-600">₹{r.cost_rs?.toLocaleString()}</td>
                  <td className="text-xs">₹{r.price_per_liter?.toFixed(2)}</td>
                  <td className={`text-xs font-bold ${isBelowExpected ? "text-red-600 bg-red-50" : "text-slate-700"}`}>
                    {actualAvg ? (
                      <span title={`Expected: ${expectedAvg || 'N/A'} km/L`}>
                        {actualAvg} km/L
                        {isBelowExpected && <span className="ml-1">⚠️</span>}
                      </span>
                    ) : "-"}
                  </td>
                  <td className="text-xs">
                    <div className="flex gap-1">
                      {r.odometer_photo_url ? (
                        <img 
                          src={r.odometer_photo_url} 
                          alt="Odometer" 
                          className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-blue-200"
                          title="Odometer Photo"
                          onClick={() => window.open(r.odometer_photo_url, '_blank')}
                        />
                      ) : null}
                      {r.fuel_pump_photo_url ? (
                        <img 
                          src={r.fuel_pump_photo_url} 
                          alt="Pump" 
                          className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-green-200"
                          title="Fuel Pump Photo"
                          onClick={() => window.open(r.fuel_pump_photo_url, '_blank')}
                        />
                      ) : null}
                      {!r.odometer_photo_url && !r.fuel_pump_photo_url && (
                        <span className="text-slate-300">-</span>
                      )}
                    </div>
                  </td>
                  <td className="text-xs capitalize">{r.payment_mode?.replace("_", " ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IssueListTab({ canManage }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchIssues = () => {
    setLoading(true);
    api.get("/vehicles/maintenance-issues/list", { params: { limit: 50 } })
      .then(res => setIssues(res.data.items || []))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchIssues(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/vehicles/maintenance-issues/${id}/status?status=${status}`);
      toast.success(`Issue marked as ${status}`);
      fetchIssues();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="dt-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Vehicle</th>
            <th>Issue Type</th>
            <th>Severity</th>
            <th>Description</th>
            <th>Status</th>
            {canManage && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {issues.length === 0 ? (
            <tr><td colSpan={canManage ? 7 : 6} className="text-center py-8 text-slate-400">No issues found</td></tr>
          ) : issues.map(i => (
            <tr key={i.id}>
              <td className="text-xs">{i.issue_date}</td>
              <td className="font-mono text-xs font-bold">{i.vehicle_number}</td>
              <td className="text-xs">{i.issue_type?.replace("_", " ")}</td>
              <td>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SEVERITY_LEVELS.find(s => s.value === i.severity)?.color}`}>
                  {i.severity?.replace("_", " ")}
                </span>
              </td>
              <td className="text-xs text-slate-600 max-w-[200px] truncate">{i.description}</td>
              <td>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  i.status === "open" ? "bg-red-100 text-red-700" :
                  i.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                  "bg-green-100 text-green-700"
                }`}>{i.status}</span>
              </td>
              {canManage && (
                <td>
                  {i.status === "open" && (
                    <button onClick={() => updateStatus(i.id, "in_progress")} className="text-[10px] text-blue-600 hover:underline">Mark In Progress</button>
                  )}
                  {i.status === "in_progress" && (
                    <button onClick={() => updateStatus(i.id, "closed")} className="text-[10px] text-green-600 hover:underline">Close</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MaintenanceListTab({ vehicles, onCreateNew }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateLog, setShowCreateLog] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get("/vehicles/maintenance-logs/list", { params: { limit: 50 } })
      .then(res => setLogs(res.data.items || []))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowCreateLog(true)}
        className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-sm font-bold text-sm hover:bg-yellow-600"
        data-testid="create-maintenance-btn"
      >
        <Plus size={16} />Create Maintenance Log
      </button>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vehicle</th>
              <th>Type</th>
              <th>Vendor</th>
              <th>Cost</th>
              <th>Photos</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No maintenance logs found</td></tr>
            ) : logs.map(l => (
              <tr key={l.id}>
                <td className="text-xs">{l.maintenance_date}</td>
                <td className="font-mono text-xs font-bold">{l.vehicle_number}</td>
                <td className="text-xs">{l.maintenance_type?.replace("_", " ")}</td>
                <td className="text-xs">{l.vendor_name || "-"}</td>
                <td className="text-xs font-bold text-green-600">₹{l.total_cost?.toLocaleString()}</td>
                <td className="text-xs">
                  <div className="flex gap-1">
                    {l.before_photo_url ? (
                      <img 
                        src={l.before_photo_url} 
                        alt="Before" 
                        className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-yellow-200"
                        title="Before Maintenance"
                        onClick={() => window.open(l.before_photo_url, '_blank')}
                      />
                    ) : null}
                    {l.after_photo_url ? (
                      <img 
                        src={l.after_photo_url} 
                        alt="After" 
                        className="w-8 h-8 object-cover rounded cursor-pointer hover:opacity-75 border border-green-200"
                        title="After Maintenance"
                        onClick={() => window.open(l.after_photo_url, '_blank')}
                      />
                    ) : null}
                    {!l.before_photo_url && !l.after_photo_url && (
                      <span className="text-slate-300">-</span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    l.status === "closed" ? "bg-green-100 text-green-700" :
                    l.status === "pending_approval" ? "bg-yellow-100 text-yellow-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>{l.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Maintenance Log Modal */}
      {showCreateLog && (
        <CreateMaintenanceLogModal
          vehicles={vehicles}
          onClose={() => setShowCreateLog(false)}
          onCreated={() => { setShowCreateLog(false); fetchData(); }}
        />
      )}
    </div>
  );
}

function DriverListTab() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignRes, empRes, vehRes] = await Promise.all([
        api.get("/vehicles/driver-assignments", { params: { is_active: true } }),
        api.get("/employees", { params: { limit: 200 } }),
        api.get("/vehicles", { params: { limit: 200 } })
      ]);
      setAssignments(assignRes.data.assignments || []);
      setEmployees(empRes.data.items || []);
      setVehicles(vehRes.data.items || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAssign = async (vehicleId, employeeId) => {
    if (!vehicleId || !employeeId) { toast.error("Please select both a vehicle and a driver"); return; }
    try {
      await api.post("/vehicles/driver-assignments", { vehicle_id: vehicleId, employee_id: employeeId });
      toast.success("Driver assigned successfully");
      setShowAssign(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign"); }
  };

  const handleRemove = async (assignmentId) => {
    if (!confirm("Remove this driver assignment?")) return;
    try {
      await api.delete(`/vehicles/driver-assignments/${assignmentId}`);
      toast.success("Assignment removed");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{assignments.length} active driver assignments</p>
        <button
          onClick={() => setShowAssign(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-sm font-bold text-sm hover:bg-green-600"
        >
          <Plus size={16} />Assign Driver
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Driver Name</th>
              <th>Original Role</th>
              <th>Mobile</th>
              <th>Vehicle</th>
              <th>Assigned By</th>
              <th>Assigned On</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No drivers assigned yet</td></tr>
            ) : assignments.map(a => (
              <tr key={a.id}>
                <td className="font-medium">{a.employee_name}</td>
                <td>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold uppercase">
                    {a.employee_role}
                  </span>
                </td>
                <td className="text-xs font-mono">{a.employee_mobile}</td>
                <td className="font-mono text-xs font-bold">{a.vehicle_registration}</td>
                <td className="text-xs">{a.assigned_by_name}</td>
                <td className="text-xs">{new Date(a.assigned_at).toLocaleDateString()}</td>
                <td>
                  <button
                    onClick={() => handleRemove(a.id)}
                    className="text-red-500 hover:text-red-700 text-xs font-bold"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign Driver Modal */}
      {showAssign && (
        <AssignDriverModal
          vehicles={vehicles}
          employees={employees}
          existingAssignments={assignments}
          onAssign={handleAssign}
          onClose={() => setShowAssign(false)}
        />
      )}
    </div>
  );
}

function AssignDriverModal({ vehicles, employees, existingAssignments, onAssign, onClose }) {
  const [vehicleId, setVehicleId] = useState("");
  const [employeeId, setEmployeeId] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vehicleId || !employeeId) {
      toast.error("Select both vehicle and employee");
      return;
    }
    onAssign(vehicleId, employeeId);
  };

  // Filter out already assigned combinations
  const assignedPairs = new Set(existingAssignments.map(a => `${a.vehicle_id}-${a.employee_id}`));

  return (
    <ModalWrapper title="Assign Driver to Vehicle" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="field-label">Select Vehicle *</label>
          <select className="field-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required>
            <option value="">Choose vehicle...</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} - {v.make} {v.model}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Select Employee *</label>
          <select className="field-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="">Choose employee...</option>
            {employees.map(e => (
              <option 
                key={e.id} 
                value={e.id}
                disabled={assignedPairs.has(`${vehicleId}-${e.id}`)}
              >
                {e.full_name} ({e.role}) - {e.mobile}
                {assignedPairs.has(`${vehicleId}-${e.id}`) ? " [Already Assigned]" : ""}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-slate-400 mt-1">Any employee can be assigned as a driver</p>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" className="flex-1 px-4 py-2 bg-green-500 text-white rounded-sm font-bold">Assign Driver</button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function TransferListTab({ circles, vehicles, onCreateNew, canApprove }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchTransfers = () => {
    setLoading(true);
    api.get("/vehicles/transfers/list", { params: { limit: 50 } })
      .then(res => setTransfers(res.data.items || []))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTransfers(); }, []);

  const handleAction = async (id, action) => {
    try {
      await api.put(`/vehicles/transfers/${id}/approve?action=${action}`);
      toast.success(`Transfer ${action}d`);
      fetchTransfers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowCreate(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-sm font-bold text-sm hover:bg-blue-600"
        data-testid="create-transfer-btn"
      >
        <Plus size={16} />Request Transfer
      </button>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="dt-table">
          <thead>
            <tr>
              <th>Vehicle</th>
              <th>From</th>
              <th>To</th>
              <th>Reason</th>
              <th>Status</th>
              {canApprove && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {transfers.length === 0 ? (
              <tr><td colSpan={canApprove ? 6 : 5} className="text-center py-8 text-slate-400">No transfer requests</td></tr>
            ) : transfers.map(t => (
              <tr key={t.id}>
                <td className="font-mono text-xs font-bold">{t.vehicle_number}</td>
                <td className="text-xs">{t.from_circle}</td>
                <td className="text-xs">{t.to_circle}</td>
                <td className="text-xs text-slate-600 max-w-[150px] truncate">{t.reason}</td>
                <td>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    t.approval_status === "approved" ? "bg-green-100 text-green-700" :
                    t.approval_status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-yellow-100 text-yellow-700"
                  }`}>{t.approval_status}</span>
                </td>
                {canApprove && (
                  <td>
                    {t.approval_status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => handleAction(t.id, "approve")} className="text-[10px] text-green-600 hover:underline">Approve</button>
                        <button onClick={() => handleAction(t.id, "reject")} className="text-[10px] text-red-600 hover:underline">Reject</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <TransferRequestModal
          vehicles={vehicles}
          circles={circles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchTransfers(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// MODALS
// =============================================================================
function CreateVehicleModal({ circles, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [form, setForm] = useState({
    // Basic Info
    registration_number: "",
    vehicle_type: "4_wheeler",
    fuel_type: "diesel",
    ownership: "company",
    vendor_name: "",
    circle_name: "",
    // Vehicle Details
    make: "",
    model: "",
    year: new Date().getFullYear(),
    current_odometer: 0,
    expected_fuel_average: null,
    // Documents
    fitness_expiry: "",
    insurance_expiry: "",
    rc_document_url: "",
    fitness_certificate_url: "",
    insurance_document_url: "",
    // Photos
    photo_front_url: "",
    photo_rear_url: "",
    photo_left_url: "",
    photo_right_url: "",
  });

  const TABS = [
    { id: "basic", label: "Basic Info", icon: Truck },
    { id: "details", label: "Vehicle Details", icon: FileText },
    { id: "documents", label: "Documents", icon: Shield },
    { id: "photos", label: "Photos", icon: Camera },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.registration_number || !form.circle_name) {
      toast.error("Registration number and circle are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/vehicles", form);
      toast.success("Vehicle added successfully");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add vehicle");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (field) => {
    // Mock photo upload - in production would use file upload
    const mockUrl = `mock://${field}-${Date.now()}.jpg`;
    setForm({ ...form, [field]: mockUrl });
    toast.success(`${field.replace(/_/g, ' ')} uploaded`);
  };

  const handleDocUpload = (field) => {
    // Mock document upload
    const mockUrl = `mock://${field}-${Date.now()}.pdf`;
    setForm({ ...form, [field]: mockUrl });
    toast.success("Document uploaded");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Add New Vehicle</h2>
            <p className="text-xs text-slate-500">Fill in all details to register a new vehicle</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 py-2 border-b border-slate-100 flex gap-1 bg-slate-50 flex-shrink-0">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                  activeTab === tab.id 
                    ? "bg-white text-orange-600 shadow-sm border border-slate-200" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-4 animate-fade-up">
                <div>
                  <label className="field-label">Registration Number *</label>
                  <input 
                    className="field-input uppercase font-mono" 
                    required 
                    placeholder="UP32XX1234"
                    value={form.registration_number} 
                    onChange={e => setForm({ ...form, registration_number: e.target.value.toUpperCase() })} 
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Enter vehicle registration number as shown on RC</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Vehicle Type *</label>
                    <select className="field-select" value={form.vehicle_type} onChange={e => setForm({ ...form, vehicle_type: e.target.value })}>
                      {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Fuel Type *</label>
                    <select className="field-select" value={form.fuel_type} onChange={e => setForm({ ...form, fuel_type: e.target.value })}>
                      {FUEL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Ownership *</label>
                    <select className="field-select" value={form.ownership} onChange={e => setForm({ ...form, ownership: e.target.value })}>
                      {OWNERSHIP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Circle *</label>
                    <select className="field-select" required value={form.circle_name} onChange={e => setForm({ ...form, circle_name: e.target.value })}>
                      <option value="">Select Circle</option>
                      {circles.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {form.ownership === "vendor" && (
                  <div>
                    <label className="field-label">Vendor Name *</label>
                    <input 
                      className="field-input" 
                      required 
                      placeholder="Enter vendor/supplier name"
                      value={form.vendor_name} 
                      onChange={e => setForm({ ...form, vendor_name: e.target.value })} 
                    />
                  </div>
                )}
              </div>
            )}

            {/* Vehicle Details Tab */}
            {activeTab === "details" && (
              <div className="space-y-4 animate-fade-up">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Make / Manufacturer</label>
                    <input 
                      className="field-input" 
                      placeholder="e.g., Maruti, Tata, Mahindra"
                      value={form.make} 
                      onChange={e => setForm({ ...form, make: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="field-label">Model</label>
                    <input 
                      className="field-input" 
                      placeholder="e.g., Swift, Nexon, Bolero"
                      value={form.model} 
                      onChange={e => setForm({ ...form, model: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Manufacturing Year</label>
                    <input 
                      type="number" 
                      className="field-input" 
                      min="2000" 
                      max={new Date().getFullYear() + 1}
                      placeholder="2023"
                      value={form.year} 
                      onChange={e => setForm({ ...form, year: parseInt(e.target.value) || 0 })} 
                    />
                  </div>
                  <div>
                    <label className="field-label">Current Odometer (km)</label>
                    <input 
                      type="number" 
                      className="field-input" 
                      min="0"
                      placeholder="0"
                      value={form.current_odometer} 
                      onChange={e => setForm({ ...form, current_odometer: parseFloat(e.target.value) || 0 })} 
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label">Expected Fuel Average (km/L)</label>
                  <input 
                    type="number" 
                    className="field-input" 
                    min="0"
                    step="0.1"
                    placeholder="e.g., 15.5"
                    value={form.expected_fuel_average || ""} 
                    onChange={e => setForm({ ...form, expected_fuel_average: parseFloat(e.target.value) || null })} 
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Expected kilometers per liter for fuel efficiency tracking</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700">
                  <Gauge size={14} className="inline mr-2" />
                  Odometer reading will be verified during first movement log
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <div className="space-y-4 animate-fade-up">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Fitness Expiry Date</label>
                    <input 
                      type="date" 
                      className="field-input" 
                      value={form.fitness_expiry} 
                      onChange={e => setForm({ ...form, fitness_expiry: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="field-label">Insurance Expiry Date</label>
                    <input 
                      type="date" 
                      className="field-input" 
                      value={form.insurance_expiry} 
                      onChange={e => setForm({ ...form, insurance_expiry: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                        <FileText size={18} className="text-slate-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">RC Document</p>
                        <p className="text-xs text-slate-400">Registration Certificate</p>
                      </div>
                    </div>
                    {form.rc_document_url ? (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 size={14} /> Uploaded
                      </span>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handleDocUpload("rc_document_url")}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded hover:bg-slate-200"
                      >
                        <Upload size={12} className="inline mr-1" />Upload
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-50 rounded flex items-center justify-center">
                        <Shield size={18} className="text-green-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Fitness Certificate</p>
                        <p className="text-xs text-slate-400">Vehicle fitness document</p>
                      </div>
                    </div>
                    {form.fitness_certificate_url ? (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 size={14} /> Uploaded
                      </span>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handleDocUpload("fitness_certificate_url")}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded hover:bg-slate-200"
                      >
                        <Upload size={12} className="inline mr-1" />Upload
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded flex items-center justify-center">
                        <FileWarning size={18} className="text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Insurance Document</p>
                        <p className="text-xs text-slate-400">Vehicle insurance policy</p>
                      </div>
                    </div>
                    {form.insurance_document_url ? (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 size={14} /> Uploaded
                      </span>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handleDocUpload("insurance_document_url")}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded hover:bg-slate-200"
                      >
                        <Upload size={12} className="inline mr-1" />Upload
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 text-sm text-yellow-700">
                  <AlertTriangle size={14} className="inline mr-2" />
                  Documents are mocked for demo. In production, files will be uploaded to cloud storage.
                </div>
              </div>
            )}

            {/* Photos Tab */}
            {activeTab === "photos" && (
              <div className="space-y-4 animate-fade-up">
                <p className="text-sm text-slate-600">Upload photos of the vehicle from all 4 angles. This helps in identification and damage assessment.</p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Front Photo */}
                  <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center hover:border-orange-300 transition-colors">
                    {form.photo_front_url ? (
                      <div className="space-y-2">
                        <div className="w-full h-24 bg-green-50 rounded flex items-center justify-center">
                          <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <p className="text-xs text-green-600 font-medium">Front Photo Uploaded</p>
                        <button 
                          type="button"
                          onClick={() => setForm({ ...form, photo_front_url: "" })}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handlePhotoUpload("photo_front_url")}
                        className="w-full"
                      >
                        <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm font-medium text-slate-600">Front View</p>
                        <p className="text-xs text-slate-400">Click to upload</p>
                      </button>
                    )}
                  </div>

                  {/* Rear Photo */}
                  <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center hover:border-orange-300 transition-colors">
                    {form.photo_rear_url ? (
                      <div className="space-y-2">
                        <div className="w-full h-24 bg-green-50 rounded flex items-center justify-center">
                          <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <p className="text-xs text-green-600 font-medium">Rear Photo Uploaded</p>
                        <button 
                          type="button"
                          onClick={() => setForm({ ...form, photo_rear_url: "" })}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handlePhotoUpload("photo_rear_url")}
                        className="w-full"
                      >
                        <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm font-medium text-slate-600">Rear View</p>
                        <p className="text-xs text-slate-400">Click to upload</p>
                      </button>
                    )}
                  </div>

                  {/* Left Photo */}
                  <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center hover:border-orange-300 transition-colors">
                    {form.photo_left_url ? (
                      <div className="space-y-2">
                        <div className="w-full h-24 bg-green-50 rounded flex items-center justify-center">
                          <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <p className="text-xs text-green-600 font-medium">Left Photo Uploaded</p>
                        <button 
                          type="button"
                          onClick={() => setForm({ ...form, photo_left_url: "" })}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handlePhotoUpload("photo_left_url")}
                        className="w-full"
                      >
                        <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm font-medium text-slate-600">Left Side</p>
                        <p className="text-xs text-slate-400">Click to upload</p>
                      </button>
                    )}
                  </div>

                  {/* Right Photo */}
                  <div className="border-2 border-dashed border-slate-200 rounded-sm p-4 text-center hover:border-orange-300 transition-colors">
                    {form.photo_right_url ? (
                      <div className="space-y-2">
                        <div className="w-full h-24 bg-green-50 rounded flex items-center justify-center">
                          <CheckCircle2 size={32} className="text-green-500" />
                        </div>
                        <p className="text-xs text-green-600 font-medium">Right Photo Uploaded</p>
                        <button 
                          type="button"
                          onClick={() => setForm({ ...form, photo_right_url: "" })}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button 
                        type="button"
                        onClick={() => handlePhotoUpload("photo_right_url")}
                        className="w-full"
                      >
                        <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-sm font-medium text-slate-600">Right Side</p>
                        <p className="text-xs text-slate-400">Click to upload</p>
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700">
                  <Camera size={14} className="inline mr-2" />
                  Photos are mocked for demo. In production, camera will capture actual images.
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
            <div className="text-xs text-slate-500">
              {activeTab === "basic" && "Step 1 of 4"}
              {activeTab === "details" && "Step 2 of 4"}
              {activeTab === "documents" && "Step 3 of 4"}
              {activeTab === "photos" && "Step 4 of 4"}
            </div>
            <div className="flex gap-3">
              <button 
                type="button" 
                onClick={onClose} 
                className="px-4 py-2 border border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              {activeTab !== "photos" ? (
                <button 
                  type="button"
                  onClick={() => {
                    const tabs = ["basic", "details", "documents", "photos"];
                    const nextIndex = tabs.indexOf(activeTab) + 1;
                    setActiveTab(tabs[nextIndex]);
                  }}
                  className="px-4 py-2 bg-slate-800 text-white rounded-sm text-sm font-bold hover:bg-slate-700 flex items-center gap-1"
                >
                  Next <ArrowRight size={14} />
                </button>
              ) : (
                <button 
                  type="submit" 
                  disabled={loading} 
                  className="px-6 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60"
                >
                  {loading ? "Adding..." : "Add Vehicle"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function VehicleDetailModal({ vehicleId, canManage, onClose, onRefresh }) {
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [movements, setMovements] = useState([]);
  const [refuels, setRefuels] = useState([]);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [fuelAverage, setFuelAverage] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [vehRes, movRes, refRes, maintRes, issueRes, fuelAvgRes] = await Promise.all([
          api.get(`/vehicles/${vehicleId}`),
          api.get("/vehicles/movements/list", { params: { vehicle_id: vehicleId, limit: 50 } }),
          api.get("/vehicles/refuels/list", { params: { vehicle_id: vehicleId, limit: 50 } }),
          api.get("/vehicles/maintenance-logs/list", { params: { vehicle_id: vehicleId, limit: 20 } }),
          api.get("/vehicles/maintenance-issues/list", { params: { vehicle_id: vehicleId, limit: 20 } }),
          api.get(`/vehicles/refuels/fuel-average/${vehicleId}`).catch(() => null)
        ]);
        setVehicle(vehRes.data);
        setMovements(movRes.data.items || []);
        setRefuels(refRes.data.items || []);
        setMaintenanceLogs(maintRes.data.items || []);
        setIssues(issueRes.data.items || []);
        if (fuelAvgRes?.data) setFuelAverage(fuelAvgRes.data);
      } catch { toast.error("Failed to load vehicle details"); }
      finally { setLoading(false); }
    };
    fetchAllData();
  }, [vehicleId]);

  if (loading) return <ModalWrapper onClose={onClose}><LoadingState /></ModalWrapper>;
  if (!vehicle) return null;

  const status = STATUS_COLORS[vehicle.status] || STATUS_COLORS.inactive;
  const tabs = [
    { id: "overview", label: "Overview", icon: FileText },
    { id: "movements", label: `Movements (${movements.length})`, icon: MapPin },
    { id: "refuels", label: `Refuels (${refuels.length})`, icon: Fuel },
    { id: "maintenance", label: `Maintenance (${maintenanceLogs.length + issues.length})`, icon: Wrench },
    { id: "photos", label: "Photos", icon: Camera }
  ];

  const totalDistance = movements.reduce((sum, m) => sum + (m.distance_traveled || m.distance_km || 0), 0);
  const totalFuelCost = refuels.reduce((sum, r) => sum + (r.cost_rs || 0), 0);
  const totalMaintCost = maintenanceLogs.reduce((sum, l) => sum + (l.total_cost || 0), 0);

  return (
    <ModalWrapper 
      title={vehicle.registration_number} 
      subtitle={`${vehicle.make || ""} ${vehicle.model || ""} • ${vehicle.vehicle_type}`} 
      onClose={onClose} 
      wide
    >
      <div className="space-y-4">
        {/* Status & Key Stats */}
        <div className="flex flex-wrap items-center gap-3">
          <span className={`px-3 py-1 rounded text-sm font-bold ${status.bg} ${status.text}`}>{status.label}</span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{vehicle.fuel_type?.toUpperCase()}</span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{vehicle.ownership}</span>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{vehicle.circle_name}</span>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-50 border border-blue-100 rounded-sm p-3 text-center">
            <p className="text-lg font-black text-blue-700">{vehicle.current_odometer?.toLocaleString() || 0}</p>
            <p className="text-[10px] text-blue-600 uppercase font-medium">Odometer (km)</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-sm p-3 text-center">
            <p className="text-lg font-black text-orange-700">{totalDistance.toLocaleString()}</p>
            <p className="text-[10px] text-orange-600 uppercase font-medium">Total Distance</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-sm p-3 text-center">
            <p className="text-lg font-black text-green-700">₹{totalFuelCost.toLocaleString()}</p>
            <p className="text-[10px] text-green-600 uppercase font-medium">Fuel Cost</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-sm p-3 text-center">
            <p className="text-lg font-black text-purple-700">₹{totalMaintCost.toLocaleString()}</p>
            <p className="text-[10px] text-purple-600 uppercase font-medium">Maint. Cost</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 pb-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-sm whitespace-nowrap ${
                activeTab === tab.id ? "bg-orange-500 text-white" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-h-[400px] overflow-y-auto">
          {activeTab === "overview" && (
            <div className="space-y-4">
              {/* Vehicle Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-700 text-xs uppercase">Vehicle Details</h4>
                  <div className="bg-slate-50 p-3 rounded-sm space-y-1 text-xs">
                    <p><span className="text-slate-500">Year:</span> <span className="font-medium">{vehicle.year || "-"}</span></p>
                    <p><span className="text-slate-500">Expected Fuel Avg:</span> <span className="font-medium">{vehicle.expected_fuel_average || "-"} km/L</span></p>
                    <p><span className="text-slate-500">Actual Fuel Avg:</span> <span className={`font-bold ${fuelAverage?.actual_average && vehicle.expected_fuel_average && fuelAverage.actual_average < vehicle.expected_fuel_average ? "text-red-600" : "text-green-600"}`}>{fuelAverage?.actual_average || "-"} km/L</span></p>
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-bold text-slate-700 text-xs uppercase">Documents</h4>
                  <div className="bg-slate-50 p-3 rounded-sm space-y-1 text-xs">
                    <p><span className="text-slate-500">Fitness Expiry:</span> <span className={`font-medium ${isExpired(vehicle.fitness_expiry) ? "text-red-600" : ""}`}>{vehicle.fitness_expiry || "-"}</span></p>
                    <p><span className="text-slate-500">Insurance Expiry:</span> <span className={`font-medium ${isExpired(vehicle.insurance_expiry) ? "text-red-600" : ""}`}>{vehicle.insurance_expiry || "-"}</span></p>
                  </div>
                </div>
              </div>

              {/* Assigned Driver */}
              {vehicle.assigned_driver && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                  <h4 className="font-bold text-blue-700 text-xs uppercase mb-2">Assigned Driver</h4>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center">
                      <User size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{vehicle.assigned_driver.full_name}</p>
                      <p className="text-xs text-slate-500">{vehicle.assigned_driver.mobile}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Open Issues Alert */}
              {issues.filter(i => i.status === "open").length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                  <h4 className="font-bold text-red-700 text-xs uppercase mb-2">Open Issues ({issues.filter(i => i.status === "open").length})</h4>
                  <div className="space-y-2">
                    {issues.filter(i => i.status === "open").slice(0, 3).map(i => (
                      <div key={i.id} className="text-xs text-red-700 bg-white p-2 rounded">
                        <span className="font-medium capitalize">{i.issue_type?.replace("_", " ")}</span> - {i.description?.substring(0, 60)}...
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "movements" && (
            <div className="space-y-2">
              {movements.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No movements recorded</p>
              ) : movements.map(m => (
                <div key={m.id} className={`bg-white border rounded-sm p-3 ${m.status === "open" ? "border-yellow-400 bg-yellow-50" : "border-slate-200"}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{m.log_date}</p>
                      <p className="text-xs text-slate-500 capitalize">{m.purpose?.replace("_", " ")} • {m.driver_name || "Unknown driver"}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {m.opening_odometer?.toLocaleString()} → {m.closing_odometer?.toLocaleString() || "..."} km
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.status === "open" ? "bg-yellow-200 text-yellow-800" : "bg-green-100 text-green-700"}`}>
                        {m.status === "open" ? "OPEN" : "CLOSED"}
                      </span>
                      {m.distance_traveled || m.distance_km ? (
                        <p className="text-sm font-bold text-orange-600 mt-1">{m.distance_traveled || m.distance_km} km</p>
                      ) : null}
                    </div>
                  </div>
                  {(m.opening_odometer_photo_url || m.closing_odometer_photo_url) && (
                    <div className="flex gap-2 mt-2">
                      {m.opening_odometer_photo_url && (
                        <img src={m.opening_odometer_photo_url} alt="Start" className="w-12 h-12 object-cover rounded border border-blue-200 cursor-pointer" onClick={() => window.open(m.opening_odometer_photo_url, '_blank')} />
                      )}
                      {m.closing_odometer_photo_url && (
                        <img src={m.closing_odometer_photo_url} alt="End" className="w-12 h-12 object-cover rounded border border-green-200 cursor-pointer" onClick={() => window.open(m.closing_odometer_photo_url, '_blank')} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === "refuels" && (
            <div className="space-y-2">
              {refuels.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No refuels recorded</p>
              ) : refuels.map(r => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{r.fuel_date}</p>
                      <p className="text-xs text-slate-500">{r.fuel_station_name || "Unknown station"} • {r.quantity_liters}L @ ₹{r.price_per_liter}/L</p>
                      <p className="text-xs text-slate-400 capitalize mt-1">{r.payment_mode?.replace("_", " ")}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">₹{r.cost_rs?.toLocaleString()}</p>
                      {r.receipt_bill_number && <p className="text-[10px] text-slate-400">#{r.receipt_bill_number}</p>}
                    </div>
                  </div>
                  {(r.odometer_photo_url || r.fuel_pump_photo_url) && (
                    <div className="flex gap-2 mt-2">
                      {r.odometer_photo_url && (
                        <img src={r.odometer_photo_url} alt="Odometer" className="w-12 h-12 object-cover rounded border border-blue-200 cursor-pointer" onClick={() => window.open(r.odometer_photo_url, '_blank')} />
                      )}
                      {r.fuel_pump_photo_url && (
                        <img src={r.fuel_pump_photo_url} alt="Pump" className="w-12 h-12 object-cover rounded border border-green-200 cursor-pointer" onClick={() => window.open(r.fuel_pump_photo_url, '_blank')} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === "maintenance" && (
            <div className="space-y-4">
              {/* Issues Section */}
              <div>
                <h4 className="font-bold text-slate-700 text-xs uppercase mb-2">Reported Issues ({issues.length})</h4>
                {issues.length === 0 ? (
                  <p className="text-center text-slate-400 py-4 text-sm">No issues reported</p>
                ) : (
                  <div className="space-y-2">
                    {issues.map(i => (
                      <div key={i.id} className={`border rounded-sm p-3 ${i.status === "open" ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm capitalize">{i.issue_type?.replace("_", " ")}</p>
                            <p className="text-xs text-slate-500">{i.description?.substring(0, 80)}...</p>
                            <p className="text-[10px] text-slate-400 mt-1">{i.reported_date}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            i.status === "open" ? "bg-red-100 text-red-700" : 
                            i.status === "in_progress" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                          }`}>{i.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Maintenance Logs Section */}
              <div>
                <h4 className="font-bold text-slate-700 text-xs uppercase mb-2">Maintenance Logs ({maintenanceLogs.length})</h4>
                {maintenanceLogs.length === 0 ? (
                  <p className="text-center text-slate-400 py-4 text-sm">No maintenance logs</p>
                ) : (
                  <div className="space-y-2">
                    {maintenanceLogs.map(l => (
                      <div key={l.id} className="bg-white border border-slate-200 rounded-sm p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm capitalize">{l.maintenance_type?.replace("_", " ")}</p>
                            <p className="text-xs text-slate-500">{l.vendor_name || "Unknown vendor"}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{l.maintenance_date}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-green-600">₹{l.total_cost?.toLocaleString()}</p>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              l.status === "closed" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                            }`}>{l.status}</span>
                          </div>
                        </div>
                        {(l.before_photo_url || l.after_photo_url) && (
                          <div className="flex gap-2 mt-2">
                            {l.before_photo_url && (
                              <img src={l.before_photo_url} alt="Before" className="w-12 h-12 object-cover rounded border border-yellow-200 cursor-pointer" onClick={() => window.open(l.before_photo_url, '_blank')} />
                            )}
                            {l.after_photo_url && (
                              <img src={l.after_photo_url} alt="After" className="w-12 h-12 object-cover rounded border border-green-200 cursor-pointer" onClick={() => window.open(l.after_photo_url, '_blank')} />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "photos" && (
            <div className="space-y-4">
              <h4 className="font-bold text-slate-700 text-xs uppercase">Vehicle Photos</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {vehicle.photo_front_url && (
                  <div className="relative cursor-pointer" onClick={() => window.open(vehicle.photo_front_url, '_blank')}>
                    <img src={vehicle.photo_front_url} alt="Front" className="w-full h-24 object-cover rounded border border-slate-200" />
                    <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Front</span>
                  </div>
                )}
                {vehicle.photo_rear_url && (
                  <div className="relative cursor-pointer" onClick={() => window.open(vehicle.photo_rear_url, '_blank')}>
                    <img src={vehicle.photo_rear_url} alt="Rear" className="w-full h-24 object-cover rounded border border-slate-200" />
                    <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Rear</span>
                  </div>
                )}
                {vehicle.photo_left_url && (
                  <div className="relative cursor-pointer" onClick={() => window.open(vehicle.photo_left_url, '_blank')}>
                    <img src={vehicle.photo_left_url} alt="Left" className="w-full h-24 object-cover rounded border border-slate-200" />
                    <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Left</span>
                  </div>
                )}
                {vehicle.photo_right_url && (
                  <div className="relative cursor-pointer" onClick={() => window.open(vehicle.photo_right_url, '_blank')}>
                    <img src={vehicle.photo_right_url} alt="Right" className="w-full h-24 object-cover rounded border border-slate-200" />
                    <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">Right</span>
                  </div>
                )}
              </div>
              {!vehicle.photo_front_url && !vehicle.photo_rear_url && !vehicle.photo_left_url && !vehicle.photo_right_url && (
                <p className="text-center text-slate-400 py-8">No vehicle photos uploaded</p>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
}

function MovementLogModal({ vehicle, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [form, setForm] = useState({
    vehicle_id: vehicle?.id,
    purpose: "installation",
    opening_odometer: vehicle?.current_odometer || 0,
    opening_odometer_photo_url: null,
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) { toast.error("Vehicle is required"); return; }
    if (!form.purpose) { toast.error("Trip purpose is required"); return; }
    if (!gps.lat) { toast.error("GPS location not captured. Please enable location services"); return; }
    setLoading(true);
    try {
      await api.post("/vehicles/movements", {
        ...form,
        start_gps_lat: gps.lat,
        start_gps_lng: gps.lng,
        opening_odometer_photo_url: "mock://odometer-photo.jpg"
      });
      toast.success("Movement started");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Start Daily Movement" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Auto-captured fields display */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1.5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Auto-captured</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-slate-500">Circle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.circle_name || "-"}</span>
            </div>
            <div>
              <span className="text-slate-500">Vehicle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.registration_number}</span>
            </div>
            <div>
              <span className="text-slate-500">Date:</span>
              <span className="ml-1 font-medium text-slate-700">{new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div>
              <span className="text-slate-500">Driver:</span>
              <span className="ml-1 font-medium text-slate-700">You</span>
            </div>
          </div>
          {gps.lat ? (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> GPS Location Captured
            </p>
          ) : (
            <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> Capturing GPS...
            </p>
          )}
        </div>

        <div>
          <label className="field-label">Purpose *</label>
          <select className="field-select" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })}>
            {MOVEMENT_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Opening Odometer (km) *</label>
          <input type="number" className="field-input" required value={form.opening_odometer}
            onChange={e => setForm({ ...form, opening_odometer: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700 flex items-center gap-2">
          <Camera size={16} />
          <span>Photo of odometer will be captured (mocked for demo)</span>
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-green-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Starting..." : "Start Movement"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function CloseMovementModal({ movement, onClose, onClosed }) {
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [form, setForm] = useState({
    closing_odometer: movement?.opening_odometer || 0,
    remarks: ""
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.closing_odometer <= movement.opening_odometer) {
      toast.error("Closing odometer must be greater than opening");
      return;
    }
    setLoading(true);
    try {
      await api.put(`/vehicles/movements/${movement.id}/close`, {
        ...form,
        end_gps_lat: gps.lat,
        end_gps_lng: gps.lng,
        closing_odometer_photo_url: "mock://closing-odometer.jpg"
      });
      toast.success("Movement closed");
      onClosed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Close Movement" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-slate-50 p-3 rounded-sm text-sm">
          <p><strong>Started at:</strong> {movement?.opening_odometer?.toLocaleString()} km</p>
          <p><strong>Purpose:</strong> {movement?.purpose?.replace("_", " ")}</p>
          {gps.lat && <p className="text-xs text-green-600 mt-1">📍 GPS captured</p>}
        </div>
        <div>
          <label className="field-label">Closing Odometer (km) *</label>
          <input type="number" className="field-input" required value={form.closing_odometer}
            onChange={e => setForm({ ...form, closing_odometer: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="field-label">Remarks</label>
          <textarea className="field-input" rows={2} value={form.remarks}
            onChange={e => setForm({ ...form, remarks: e.target.value })} />
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm text-green-700">
          <strong>Estimated Distance:</strong> {Math.max(0, form.closing_odometer - movement.opening_odometer).toFixed(1)} km
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Closing..." : "Close Movement"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function RefuelLogModal({ vehicle, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [form, setForm] = useState({
    vehicle_id: vehicle?.id,
    quantity_liters: "",
    cost_rs: "",
    odometer_reading: vehicle?.current_odometer || 0,
    payment_mode: "company_upi",
    fuel_station_name: "",
    bill_number: ""
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quantity_liters || parseFloat(form.quantity_liters) <= 0) { toast.error("Fuel quantity is required"); return; }
    if (!form.cost_rs || parseFloat(form.cost_rs) <= 0) { toast.error("Fuel cost is required"); return; }
    if (!form.odometer_reading) { toast.error("Odometer reading is required"); return; }
    setLoading(true);
    try {
      await api.post("/vehicles/refuels", {
        ...form,
        quantity_liters: parseFloat(form.quantity_liters),
        cost_rs: parseFloat(form.cost_rs),
        odometer_reading: parseFloat(form.odometer_reading),
        gps_lat: gps.lat,
        gps_lng: gps.lng,
        odometer_photo_url: "mock://odometer.jpg",
        fuel_pump_photo_url: "mock://fuel-pump.jpg"
      });
      toast.success("Refuel logged");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Log Refuel" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Auto-captured fields display */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1.5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Auto-captured</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-slate-500">Circle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.circle_name || "-"}</span>
            </div>
            <div>
              <span className="text-slate-500">Vehicle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.registration_number}</span>
            </div>
            <div>
              <span className="text-slate-500">Date:</span>
              <span className="ml-1 font-medium text-slate-700">{new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div>
              <span className="text-slate-500">Fuel Type:</span>
              <span className="ml-1 font-medium text-slate-700 uppercase">{vehicle?.fuel_type}</span>
            </div>
          </div>
          {gps.lat ? (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> GPS Location Captured
            </p>
          ) : (
            <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> Capturing GPS...
            </p>
          )}
        </div>

        {/* Fuel Station Details */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Fuel Station Name *</label>
            <input 
              type="text" 
              className="field-input" 
              required
              placeholder="e.g., HP Petrol Pump"
              value={form.fuel_station_name} 
              onChange={e => setForm({ ...form, fuel_station_name: e.target.value })} 
            />
          </div>
          <div>
            <label className="field-label">Bill/Receipt Number</label>
            <input 
              type="text" 
              className="field-input" 
              placeholder="e.g., INV-12345"
              value={form.bill_number} 
              onChange={e => setForm({ ...form, bill_number: e.target.value })} 
            />
          </div>
        </div>

        {/* Quantity and Cost */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label">Quantity (Liters) *</label>
            <input type="number" step="0.1" className="field-input" required value={form.quantity_liters}
              onChange={e => setForm({ ...form, quantity_liters: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Total Cost (₹) *</label>
            <input type="number" step="0.01" className="field-input" required value={form.cost_rs}
              onChange={e => setForm({ ...form, cost_rs: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="field-label">Odometer Reading (km) *</label>
          <input type="number" className="field-input" required value={form.odometer_reading}
            onChange={e => setForm({ ...form, odometer_reading: e.target.value })} />
        </div>

        <div>
          <label className="field-label">Payment Mode *</label>
          <select className="field-select" value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })}>
            {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {/* Calculated price per liter */}
        {form.quantity_liters && form.cost_rs && (
          <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-sm text-purple-700 flex items-center justify-between">
            <span>Price per liter:</span>
            <span className="font-bold text-lg">₹{(parseFloat(form.cost_rs) / parseFloat(form.quantity_liters)).toFixed(2)}</span>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700 flex items-center gap-2">
          <Camera size={16} />
          <span>Odometer & fuel pump photos will be captured (mocked for demo)</span>
        </div>

        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Saving..." : "Log Refuel"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function MaintenanceIssueModal({ vehicle, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState({ lat: null, lng: null });
  const [form, setForm] = useState({
    vehicle_id: vehicle?.id,
    issue_type: "general_service",
    description: "",
    severity: "can_drive",
    current_odometer: vehicle?.current_odometer || 0
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description) {
      toast.error("Description is required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/vehicles/maintenance-issues", {
        ...form,
        gps_lat: gps.lat,
        gps_lng: gps.lng,
        audio_recording_url: "mock://audio.mp3",
        photo_url_1: "mock://issue-photo1.jpg"
      });
      toast.success("Issue reported");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Report Maintenance Issue" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Auto-captured fields display */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1.5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Auto-captured</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-slate-500">Circle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.circle_name || "-"}</span>
            </div>
            <div>
              <span className="text-slate-500">Vehicle:</span>
              <span className="ml-1 font-medium text-slate-700">{vehicle?.registration_number}</span>
            </div>
            <div>
              <span className="text-slate-500">Date:</span>
              <span className="ml-1 font-medium text-slate-700">{new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div>
              <span className="text-slate-500">Reporter:</span>
              <span className="ml-1 font-medium text-slate-700">You (Driver)</span>
            </div>
          </div>
          {gps.lat ? (
            <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> GPS Location Captured
            </p>
          ) : (
            <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
              <MapPin size={12} /> Capturing GPS...
            </p>
          )}
        </div>

        <div>
          <label className="field-label">Issue Type *</label>
          <select className="field-select" value={form.issue_type} onChange={e => setForm({ ...form, issue_type: e.target.value })}>
            {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <label className="field-label">Description *</label>
          <textarea className="field-input" rows={3} required placeholder="Describe the issue in detail..."
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <label className="field-label">Severity *</label>
          <div className="grid grid-cols-1 gap-2">
            {SEVERITY_LEVELS.map(s => (
              <label key={s.value} className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${form.severity === s.value ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="severity" value={s.value} checked={form.severity === s.value}
                  onChange={e => setForm({ ...form, severity: e.target.value })} className="accent-orange-500" />
                <div>
                  <span className={`text-sm font-medium ${s.color.split(" ")[0]}`}>{s.label}</span>
                  <p className="text-xs text-slate-400">
                    {s.value === "can_drive" && "Vehicle is operational, minor issue"}
                    {s.value === "needs_service_soon" && "Should be serviced within 1-2 days"}
                    {s.value === "cannot_drive" && "Vehicle is not safe to drive"}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label">Current Odometer (km)</label>
          <input type="number" className="field-input" value={form.current_odometer}
            onChange={e => setForm({ ...form, current_odometer: parseFloat(e.target.value) || 0 })} />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700">
          <div className="flex items-center gap-2 mb-1">
            <Mic size={14} />
            <span className="font-medium">Audio & Photos</span>
          </div>
          <p className="text-xs">Voice recording and issue photos will be captured (mocked for demo)</p>
        </div>

        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Reporting..." : "Report Issue"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function CreateMaintenanceLogModal({ vehicles, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    maintenance_type: "general_service",
    maintenance_date: new Date().toISOString().slice(0, 10),
    description: "",
    vendor_name: "",
    labor_cost: 0,
    parts_cost: 0,
    notes: ""
  });

  const MAINTENANCE_TYPES = [
    { value: "preventive", label: "Preventive Maintenance" },
    { value: "general_service", label: "General Service" },
    { value: "accident_repair", label: "Accident Repair" },
    { value: "breakdown_repair", label: "Breakdown Repair" },
    { value: "tyre_change", label: "Tyre Change" },
    { value: "battery_replacement", label: "Battery Replacement" },
    { value: "oil_change", label: "Oil Change" },
    { value: "other", label: "Other" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) {
      toast.error("Please select a vehicle");
      return;
    }
    setLoading(true);
    try {
      await api.post("/vehicles/maintenance-logs", {
        ...form,
        total_cost: (form.labor_cost || 0) + (form.parts_cost || 0)
      });
      toast.success("Maintenance log created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create maintenance log");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Create Maintenance Log" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="field-label">Vehicle *</label>
          <select className="field-select" required value={form.vehicle_id}
            onChange={e => setForm({ ...form, vehicle_id: e.target.value })}>
            <option value="">Select Vehicle</option>
            {vehicles?.map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} - {v.make} {v.model}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Maintenance Type *</label>
            <select className="field-select" required value={form.maintenance_type}
              onChange={e => setForm({ ...form, maintenance_type: e.target.value })}>
              {MAINTENANCE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label">Date *</label>
            <input type="date" className="field-input" required value={form.maintenance_date}
              onChange={e => setForm({ ...form, maintenance_date: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="field-label">Description</label>
          <textarea className="field-input" rows={2} placeholder="What maintenance was performed..."
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <label className="field-label">Vendor/Garage Name</label>
          <input type="text" className="field-input" placeholder="e.g., Authorized Service Center"
            value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Labor Cost (₹)</label>
            <input type="number" className="field-input" min="0" step="1"
              value={form.labor_cost} onChange={e => setForm({ ...form, labor_cost: parseFloat(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="field-label">Parts Cost (₹)</label>
            <input type="number" className="field-input" min="0" step="1"
              value={form.parts_cost} onChange={e => setForm({ ...form, parts_cost: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <p className="text-sm font-medium text-slate-700">
            Total Cost: <span className="text-green-600">₹{((form.labor_cost || 0) + (form.parts_cost || 0)).toLocaleString()}</span>
          </p>
        </div>

        <div>
          <label className="field-label">Notes</label>
          <textarea className="field-input" rows={2} placeholder="Any additional notes..."
            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-700">
          <div className="flex items-center gap-2 mb-1">
            <Camera size={14} />
            <span className="font-medium">Before/After Photos</span>
          </div>
          <p className="text-xs">Photo uploads will be available (mocked for demo)</p>
        </div>

        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Creating..." : "Create Log"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

function TransferRequestModal({ vehicles, circles, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    to_circle: "",
    reason: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.to_circle || !form.reason) {
      toast.error("All fields are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/vehicles/transfers", form);
      toast.success("Transfer request submitted");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper title="Request Vehicle Transfer" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="field-label">Vehicle *</label>
          <select className="field-select" value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}>
            <option value="">Select vehicle</option>
            {vehicles.filter(v => v.status === "active").map(v => (
              <option key={v.id} value={v.id}>{v.registration_number} ({v.circle_name})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Transfer To Circle *</label>
          <select className="field-select" value={form.to_circle} onChange={e => setForm({ ...form, to_circle: e.target.value })}>
            <option value="">Select circle</option>
            {circles.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Reason *</label>
          <textarea className="field-input" rows={2} required placeholder="Why is this transfer needed?"
            value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
        </div>
        <div className="flex gap-3 pt-4">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">Cancel</button>
          <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-sm font-bold disabled:opacity-60">
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}

// =============================================================================
// HELPERS
// =============================================================================
function ModalWrapper({ title, subtitle, wide, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 pt-16 overflow-y-auto">
      <div className={`bg-white rounded-sm shadow-xl mb-8 ${wide ? "w-full max-w-2xl" : "w-full max-w-md"}`}>
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-white">
          <div>
            {title && <h3 className="text-lg font-bold">{title}</h3>}
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
      <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
      <p className="text-slate-500 text-sm">Loading...</p>
    </div>
  );
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return dateStr < new Date().toISOString().slice(0, 10);
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const today = new Date();
  const expiry = new Date(dateStr);
  const diffDays = (expiry - today) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 30;
}
